import { useCallback } from 'react';
import { useGenericBi } from './use-generic-bi';
import type { DeliveryOption } from './send-verification-code';

/**
 * Reports how a request for a verification code ended.
 *
 * It exists because it replaces a SERVER-side line. The send used to go through our own API route, which
 * wrote `[onboarding-funnel] step=send-code outcome=…`, and every measurement of the shared-budget bug was
 * counted off that line — including the four days on which it refused 135 people. The send now goes straight
 * from the browser to the endpoint, so no pod sees it and the line is gone; without this event the refusal
 * rate is unmeasurable again, which is how the bug stayed invisible for as long as it did.
 *
 * `outcome` is a closed vocabulary so the field stays aggregatable — the reason the server line spelled its
 * outcomes out rather than interpolating a message. The number and the verificationId are NEVER included.
 */
export type SendCodeOutcomeName =
  /** The endpoint accepted it and a code is on its way. The funnel's denominator. */
  | 'sent'
  /** Refused by the edge (429). Not this user resending too often — see `send-verification-code.ts`. */
  | 'refused'
  /** Reached the endpoint, which declined to send — WhatsApp unavailable for this number, typically. */
  | 'not-sent'
  /** Never got an answer: it threw, or answered a status the sender does not know how to read. */
  | 'failed';

const REGION = 'phone-verification-send';

export function useSendCodeBi(componentParentName: string) {
  const { logLoadFinish } = useGenericBi(componentParentName);
  return useCallback(
    (outcome: SendCodeOutcomeName, deliveryOption: DeliveryOption) =>
      logLoadFinish(
        REGION,
        { isSuccessful: outcome === 'sent' },
        { additionalFields: { outcome, delivery_option: deliveryOption } },
      ),
    [logLoadFinish],
  );
}
